import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { BaseView, BaseText } from '@/components/base'
import { CardItem } from '@/components/CardItem'
import { AppIcons } from '@/components/AppIcons'
import { ActivityItem } from '@/components/ActivityItem'
import { SkeletonCard } from '@/components/SkeletonCard'
import {
  WalletCurrency,
  useGetTransactions,
  useGetWallet,
  transactionServices,
} from '@/domain'
import { useTheme } from '@/hooks/useTheme'
import { responsiveAdapter } from '@/utils'
import { formatTransactionItem } from '@/utils/formatTransactionItem'

type LatestActivitiesProps = WalletCurrency

export const LatestActivities: React.FC<LatestActivitiesProps> = ({
  code = '',
  displayCurrencyCode,
  ...currency
}) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const theme = useTheme()

  // Fetch transactions filtered by currency code (like in RN version)
  const { data: transactionsData, isLoading } = useGetTransactions({
    currencies: code ? [code] : undefined,
    page_size: 3, // Show only last 3 transactions like in RN
  })

  // Get wallet data for currency info
  const { data: walletData } = useGetWallet()

  // Create currency map for enhanced transaction display (like in RN version)
  const currencyMap = useMemo(() => {
    if (!walletData?.completeCurrencies) return {}

    return walletData.completeCurrencies.reduce(
      (acc, currency) => {
        const currencyCode = currency.code?.toLowerCase()?.trim()
        if (currencyCode) {
          acc[currencyCode] = {
            icon: currency.icon,
            avatarBGRefColor: currency.avatarBGRefColor,
            name: currency.name,
            symbol: currency.symbol,
          }
        }
        return acc
      },
      {} as Record<
        string,
        {
          icon?: string
          avatarBGRefColor?: string | [string, string, ...string[]]
          name?: string
          symbol?: string
        }
      >,
    )
  }, [walletData?.completeCurrencies])

  // Enhance transactions with currency data (like in RN version)
  const handledTransactionsList = useMemo(() => {
    if (!transactionsData?.pages) return []

    const allTransactions = transactionsData.pages.flatMap(
      (page) => page.transactions,
    )

    return allTransactions.map((transaction) => {
      // Determine currency code based on transaction type (like in RN)
      const isConvert = transaction.type === 'swap_convert'
      const isCardPurchase = transaction.type === 'card_write_off'

      const currencyCode = isConvert
        ? transaction.subject_swap?.credit_amount?.currency
            ?.toLowerCase()
            ?.trim()
        : transaction.amount?.currency?.toLowerCase()?.trim()

      const currencyDetails = currencyCode ? currencyMap[currencyCode] : {}

      // Enhanced transaction with currency data (using same logic as TransactionsList)
      const enhancedTransaction = {
        ...transaction,
        currencyIcon: currencyDetails?.icon,
        currencyColor: isCardPurchase
          ? theme.colors.background.cardItemRoot
          : currencyDetails?.avatarBGRefColor,
        currencyName: currencyDetails?.name,
        currencySymbol: currencyDetails?.symbol,
        // Legacy fields for compatibility
        currencyIconUrl: currencyDetails?.icon,
        currencyColorReference: isCardPurchase
          ? theme.colors.background.cardItemRoot
          : currencyDetails?.avatarBGRefColor,
      }

      const finalEnhancedTransaction = {
        ...enhancedTransaction,
        // Add formatted fields that ActivityItem expects
        formattedAmount:
          transactionServices.formatTransactionAmount(transaction).amount,
        formattedDate: new Date(transaction.created_at).toLocaleDateString(),
        formattedTime: new Date(transaction.created_at).toLocaleTimeString(),
        subTitle: '', // TODO: implement getTransactionSubtitle if needed
      }

      // Format transaction using RN-style formatter
      const formattedParams = formatTransactionItem({
        item: finalEnhancedTransaction,
        t,
      })

      // Merge enhanced transaction with formatted params
      // Prioritize formattedParams for correct avatar display
      return {
        ...finalEnhancedTransaction,
        ...formattedParams,
        // Ensure uri from formattedParams is not overwritten
        uri: formattedParams.uri,
        name: formattedParams.name,
        backgroundColor: formattedParams.backgroundColor,
      }
    })
  }, [transactionsData?.pages, currencyMap, theme, t])

  const handleOpenCryptoActivities = () => {
    navigate('/currency-details/activities', {
      state: {
        code,
        displayCurrencyCode,
        currencyFilter: code,
        ...currency,
      },
    })
  }

  // Loading state (skeleton like in RN)
  if (isLoading) {
    return <SkeletonCard height={228.33} radius={24} />
  }

  return (
    <>
      {/* <Separator /> */}

      <CardItem.Root
        style={{
          paddingTop: `${responsiveAdapter(20)}px`,
          paddingBottom: `${responsiveAdapter(20)}px`,
          paddingLeft: 0,
          paddingRight: 0,
          marginBottom: `${responsiveAdapter(22)}px`,
        }}
        onPress={handleOpenCryptoActivities}
      >
        <BaseView fullWidth gap={12}>
          <BaseText
            fontSize={20}
            fontFamily="semibold"
            style={{
              paddingLeft: `${responsiveAdapter(12)}px`,
              paddingRight: `${responsiveAdapter(12)}px`,
            }}
          >
            {t('translation:activity')}
          </BaseText>

          {/* Empty state */}
          {handledTransactionsList.length === 0 && (
            <BaseView row gap={8} align="center" px={12}>
              <AppIcons name="clock" color="icon.default" size={20} />
              <BaseText color="label.description">
                {t('translation:emptyTransactions')}
              </BaseText>
            </BaseView>
          )}

          {/* Transactions list */}
          {handledTransactionsList.length > 0 && (
            <BaseView
              style={{
                ...(handledTransactionsList.length > 2 && {
                  maxHeight: `${responsiveAdapter(72) * 3}px`,
                  overflowY: 'hidden',
                }),
              }}
            >
              {handledTransactionsList.slice(0, 3).map((transaction) => (
                <ActivityItem
                  key={transaction.transaction_id}
                  height={responsiveAdapter(72)}
                  isLastActivities={true}
                  {...transaction}
                />
              ))}
            </BaseView>
          )}
        </BaseView>
      </CardItem.Root>
    </>
  )
}
